import { useState } from "react";
import { ChevronDown, ChevronUp, ArrowRight, Shield, FilterX } from "lucide-react";
import type { RansomwareActor } from "../../../api/status";
import { formatNumber, formatPercent } from "../../../utils/ransomwareUtils";

interface GroupsLeaderboardProps {
  actors: RansomwareActor[];
  totalAttacks: number;
  selectedGroup: string | null;
  selectedSector: string | null;
  onSelectGroup: (groupName: string | null) => void;
  onOpenGroupDrawer: (groupName: string) => void;
  onClearSectorFilter?: () => void;
}

export default function GroupsLeaderboard({
  actors,
  totalAttacks,
  selectedGroup,
  selectedSector,
  onOpenGroupDrawer,
  onClearSectorFilter,
}: GroupsLeaderboardProps) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const safeActors = Array.isArray(actors) ? actors : [];
  const visibleActors = expanded ? safeActors : safeActors.slice(0, 5);

  return (
    <div
      className="rw-leaderboard-panel"
      style={{
        background: "var(--dash-card-bg, rgba(15, 23, 42, 0.6))",
        border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
        borderRadius: 12,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "440px", // Fixed minimum height so it never collapses in any state
        backdropFilter: "blur(12px)",
        boxSizing: "border-box",
      }}
    >
      <div>
        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h3
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--dash-text-primary, #f8fafc)",
                margin: 0,
              }}
            >
              Most Active Threat Groups
            </h3>
            <p
              style={{
                fontSize: "0.75rem",
                color: "var(--dash-text-muted, #94a3b8)",
                marginTop: 2,
                margin: 0,
              }}
            >
              Ransomware syndicates ranked by disclosure frequency
            </p>
          </div>

          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "var(--dash-text-muted, #94a3b8)",
              letterSpacing: "0.02em",
            }}
          >
            {safeActors.length} Syndicates
          </span>
        </div>

        {/* EMPTY STATE FOR CROSS-FILTERED SECTOR WITH 0 GROUPS */}
        {safeActors.length === 0 ? (
          <div
            style={{
              padding: "48px 16px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              flex: 1,
            }}
          >
            <FilterX size={32} style={{ color: "var(--dash-text-muted, #64748b)" }} />
            <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--dash-text-primary, #f8fafc)" }}>
              {selectedSector
                ? `No groups recorded for ${selectedSector} in this period`
                : "No threat groups recorded in this timeframe"}
            </div>
            {selectedSector && onClearSectorFilter && (
              <button
                onClick={onClearSectorFilter}
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: "#ffffff",
                  background: "var(--dash-indigo, #6366F1)",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 14px",
                  cursor: "pointer",
                  transition: "background 0.15s ease",
                }}
              >
                Clear filter
              </button>
            )}
          </div>
        ) : (
          /* LEADERBOARD LIST */
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {visibleActors.map((actor, idx) => {
              const actorName = actor?.name || `Syndicate #${idx + 1}`;
              const isSelected = selectedGroup === actorName;
              const rankNum = typeof actor?.rank === "number" ? actor.rank : idx + 1;
              const attacksCount = typeof actor?.attacks === "number" ? actor.attacks : 0;
              
              // Share % = count / total (never blank, NaN or fake)
              const sharePct = totalAttacks > 0
                ? Number(((attacksCount / totalAttacks) * 100).toFixed(1))
                : (typeof actor?.share_pct === "number" ? actor.share_pct : 0);

              return (
                <div
                  key={actorName}
                  onClick={() => onOpenGroupDrawer(actorName)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenGroupDrawer(actorName);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`View intel for ransomware group ${actorName}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 1fr 100px 32px",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 16px",
                    borderRadius: 8,
                    background: isSelected
                      ? "rgba(0, 102, 224, 0.15)"
                      : "var(--dash-subcard-bg, rgba(255, 255, 255, 0.03))",
                    border: `1px solid ${
                      isSelected
                        ? "rgba(0, 102, 224, 0.4)"
                        : "var(--dash-card-border, rgba(255, 255, 255, 0.06))"
                    }`,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                  }}
                  className="rw-actor-row"
                >
                  {/* RANK NUMBER */}
                  <span
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 800,
                      color: "var(--dash-text-muted, #64748b)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    #{rankNum.toString().padStart(2, "0")}
                  </span>

                  {/* GROUP NAME */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, overflow: "hidden" }}>
                    <Shield size={16} style={{ color: "var(--dash-indigo, #6366F1)", flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        color: "var(--dash-text-primary, #f8fafc)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {actorName}
                    </span>
                  </div>

                  {/* ATTACK COUNT & SHARE % */}
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "0.92rem",
                        fontWeight: 800,
                        fontVariantNumeric: "tabular-nums",
                        color: "var(--dash-text-primary, #f8fafc)",
                      }}
                    >
                      {formatNumber(attacksCount)}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "var(--dash-indigo, #818cf8)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatPercent(sharePct)}
                    </div>
                  </div>

                  {/* ACTION CHEVRON */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <ArrowRight
                      size={16}
                      style={{ color: "var(--dash-text-muted, #94a3b8)", transition: "transform 0.15s ease" }}
                      className="rw-row-arrow"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* VIEW ALL EXPAND BUTTON */}
      {safeActors.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            width: "100%",
            padding: "10px",
            marginTop: 12,
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "var(--dash-indigo, #818cf8)",
            background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.03))",
            border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
            cursor: "pointer",
            borderRadius: 8,
            transition: "all 0.15s ease",
          }}
        >
          <span>{expanded ? "Show top 5 syndicates" : `View all ${safeActors.length} syndicates`}</span>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}
    </div>
  );
}
